import { ArrowLeftIcon, PhotoIcon, UserCircleIcon, TicketIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import 'leaflet/dist/leaflet.css'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { GET_BOAT_DAY_PASS_PRICE } from '../../../graphql/booking/queries'
import { useMutation } from '@apollo/client'
import { ADD_DAY_PASS_PRICE, UPDATE_DAY_PASS_PRICE } from '../../../graphql/booking/mutations'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Flatpickr from 'react-flatpickr'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { GET_CHART_BOAT_STATUS, GET_CHARTER_BOAT_CONFIGS_LIST } from 'graphql/charterBoats/queries'
import { CREATE_CHARTER_BOAT_PRICE, UPDATE_CHARTER_BOAT_PRICE } from 'graphql/charterBoats/mutations'
import { BoatNameDropdown } from 'components/custom/BoatNameDropdown'
import { useLocation } from 'react-router-dom'

export default function ManageCharterBoatForm() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const location = useLocation()

    const [passData, setPassData] = useState(null)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [dialogError, setDialogError] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)
    const selectedConfigId = location.state?.configId
    const statusMap = {
        ACT: { color: 'emerald', label: 'Active' },
        EXP: { color: 'amber', label: 'Expired' },
        INA: { color: 'rose', label: 'Inactive' },
    }

    const [getPassPriceList, { loading: passPriceListLoading, error: passPriceListError }] = useLazyQuery(
        GET_CHARTER_BOAT_CONFIGS_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                const configs = data?.getCharterBoatConfigs?.charter_boat_configs || []

                const selectedConfig = configs.find(config => config.config_id === selectedConfigId)

                setPassData(selectedConfig || null)
            },
        }
    )

    const searchBoatPass = () => {
        getPassPriceList({
            variables: {
                page: {
                    limit: 10,
                    offset: 0,
                },
                filter: {
                    to_date: null,
                    status_code: null,
                    from_date: null,
                    boat_id: id,
                },
            },
        })
    }

    useEffect(() => {
        if (id) {
            searchBoatPass()
        } else {
            setPassData(null)
        }
    }, [id])

    const handleBack = () => {
        navigate('/price-config/charter-boat-price')
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    const [addNewBoatDayPass, { loading: addLoading }] = useMutation(CREATE_CHARTER_BOAT_PRICE, {
        onCompleted: data => {
            if (data?.createCharterBoatConfig.code === '200' && data?.createCharterBoatConfig.success === true) {
                CreateBoatDayPassFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                CreateBoatDayPassFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setDialogError(data?.addBoatDayPassPrice.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            CreateBoatDayPassFormValidation.setSubmitting(false)
            setDialogError(error.message)
            setShowErrDialog(true)
        },
    })

    const [updateBoatDayPass, { loading: updateLoading }] = useMutation(UPDATE_CHARTER_BOAT_PRICE, {
        onCompleted: data => {
            if (data?.updateCharterBoatConfig.code === '200' && data?.updateCharterBoatConfig.success === true) {
                CreateBoatDayPassFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                CreateBoatDayPassFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setDialogError(data?.updateCharterBoatConfig.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            CreateBoatDayPassFormValidation.setSubmitting(false)
            setDialogError(error.message)
            setShowErrDialog(true)
        },
    })

    const CreateBoatDayPassFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            // name: {
            //     en: passData?.boat?.name?.en || '',
            //     ar: passData?.boat?.name?.ar || '',
            // },
            // description: {
            //     en: passData?.description?.en || '',
            //     ar: passData?.description?.ar || '',
            // },
            from_date: passData?.from_date || '',
            to_date: passData?.to_date || '',
            status_code: passData?.status?.code || 'ACT',
            price: {
                currency: passData?.price?.currency || 'BHD',
                amount: passData?.price?.amount || null,
            },
            boat_id: passData?.boat_id || '',
        },
        validationSchema: Yup.object({
            boat_id: Yup.string().required('Boat is required'),
            from_date: Yup.string().required('From date is required'),
            to_date: Yup.string().required('To date is required'),
            status_code: Yup.string().required('Status is required'),
            price: Yup.object({
                currency: Yup.string().required('Currency is required'),
                amount: Yup.number()
                    .typeError('Amount must be a number')
                    .min(0, 'Amount cannot be negative')
                    .required('Amount is required'),
            }),
        }),
        onSubmit: values => {
            setSubmitLoading(true)
            CreateBoatDayPassFormValidation.setSubmitting(true)
            console.log(values)
            submitBoat(values)
        },
    })

    const submitBoat = async values => {
        const priceData = {
            ...values,
            boat_id: values.boat_id,
            from_date: values.from_date,
            to_date: values.to_date,
            status_code: values.status_code,
            price: {
                currency: values.price.currency,
                amount: parseFloat(values.price.amount),
            },
        }

        try {
            if (id) {
                updateBoatDayPass({
                    variables: {
                        charterBoatConfig: priceData,
                        configId: passData?.config_id,
                    },
                })
            } else {
                addNewBoatDayPass({
                    variables: {
                        charterBoatConfig: priceData,
                    },
                })
            }
        } catch (error) {
            CreateBoatDayPassFormValidation.setSubmitting(false)
        }
    }

    return (
        <>
            <div className="mb-8 flex justify-between items-center">
                <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
            </div>
            <form onSubmit={CreateBoatDayPassFormValidation.handleSubmit}>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                                {t('Charter Boat Information')}
                            </h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('Select a charter boat from the dropdown')}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-full">
                                <label htmlFor="captain" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Boat')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <BoatNameDropdown
                                        fields={CreateBoatDayPassFormValidation.getFieldProps('boat_id')}
                                        purpose="add"
                                        status="ACT"
                                        // onBoatSelect={handleBoatSelect}
                                    />
                                    {CreateBoatDayPassFormValidation.touched.boat_id &&
                                        CreateBoatDayPassFormValidation.errors.boat_id && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBoatDayPassFormValidation.errors.boat_id}
                                            </p>
                                        )}
                                </div>
                            </div>
                        </div>

                        {/* <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-3">
                                <label htmlFor="name.en" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Name (English)')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="name.en"
                                        name="name.en"
                                        type="text"
                                        placeholder="Enter name in English"
                                        value={CreateBoatDayPassFormValidation.values.name.en}
                                        onChange={CreateBoatDayPassFormValidation.handleChange}
                                        onBlur={CreateBoatDayPassFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatDayPassFormValidation?.errors?.name?.en &&
                                    CreateBoatDayPassFormValidation?.touched?.name?.en ? (
                                        <div className="text-red text-sm">
                                            {CreateBoatDayPassFormValidation?.errors?.name?.en}
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label htmlFor="name.ar" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Name (Arabic)')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="name.ar"
                                        name="name.ar"
                                        type="text"
                                        placeholder="أدخل الاسم بالعربية"
                                        value={CreateBoatDayPassFormValidation?.values?.name?.ar}
                                        onChange={CreateBoatDayPassFormValidation?.handleChange}
                                        onBlur={CreateBoatDayPassFormValidation?.handleBlur}
                                        dir="rtl"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="description.en"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Description (English)')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <textarea
                                        id="description.en"
                                        name="description.en"
                                        rows={3}
                                        placeholder="Enter description in English"
                                        value={CreateBoatDayPassFormValidation.values.description.en}
                                        onChange={CreateBoatDayPassFormValidation.handleChange}
                                        onBlur={CreateBoatDayPassFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="description.ar"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Description (Arabic)')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <textarea
                                        id="description.ar"
                                        name="description.ar"
                                        rows={3}
                                        placeholder="أدخل الوصف بالعربية"
                                        value={CreateBoatDayPassFormValidation?.values?.description?.ar}
                                        onChange={CreateBoatDayPassFormValidation?.handleChange}
                                        onBlur={CreateBoatDayPassFormValidation?.handleBlur}
                                        dir="rtl"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>
                        </div>
                    </div> */}
                    </div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                                {t('Charter Boat Validity')}
                            </h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('Enter the validity date of the charter boat')}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="from_date"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Start From')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <Flatpickr
                                        value={CreateBoatDayPassFormValidation.values.from_date}
                                        onChange={date =>
                                            CreateBoatDayPassFormValidation.setFieldValue(
                                                'from_date',
                                                date[0] ? date[0].toLocaleDateString('en-CA').replace(/\//g, '-') : ''
                                            )
                                        }
                                        options={{
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                            dateFormat: 'Y/m/d',
                                            value: CreateBoatDayPassFormValidation.values.from_date,
                                            defaultDate: CreateBoatDayPassFormValidation.values.from_date,
                                            maxDate: CreateBoatDayPassFormValidation.values.to_date,
                                        }}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatDayPassFormValidation.errors.from_date &&
                                    CreateBoatDayPassFormValidation.touched.from_date ? (
                                        <div className="text-red text-sm">
                                            {CreateBoatDayPassFormValidation.errors.from_date}
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label htmlFor="to_date" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('End To')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <Flatpickr
                                        value={CreateBoatDayPassFormValidation.values.to_date}
                                        onChange={date =>
                                            CreateBoatDayPassFormValidation.setFieldValue(
                                                'to_date',
                                                date[0] ? date[0].toLocaleDateString('en-CA').replace(/\//g, '-') : ''
                                            )
                                        }
                                        options={{
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                            dateFormat: 'Y/m/d',
                                            value: CreateBoatDayPassFormValidation.values.to_date,
                                            defaultDate: CreateBoatDayPassFormValidation.values.to_date,
                                            minDate: CreateBoatDayPassFormValidation.values.from_date,
                                        }}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatDayPassFormValidation.errors.to_date &&
                                    CreateBoatDayPassFormValidation.touched.to_date ? (
                                        <div className="text-red text-sm">
                                            {CreateBoatDayPassFormValidation.errors.to_date}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Price Information')}
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Enter the detailed price of the charter for all categories')}
                                </p>
                            </div>

                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                {/* Amount (First Column) */}
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Amount')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <div className="mt-2">
                                        <input
                                            name="price.amount"
                                            type="number"
                                            value={CreateBoatDayPassFormValidation.values.price.amount}
                                            onChange={CreateBoatDayPassFormValidation.handleChange}
                                            onBlur={CreateBoatDayPassFormValidation.handleBlur}
                                            placeholder="Enter amount…"
                                            className="block w-full rounded-md border-gray-300 p-2"
                                        />
                                    </div>
                                    {CreateBoatDayPassFormValidation.touched.price?.amount &&
                                        CreateBoatDayPassFormValidation.errors.price?.amount && (
                                            <div className="text-red text-sm mt-1">
                                                {CreateBoatDayPassFormValidation.errors.price.amount}
                                            </div>
                                        )}
                                </div>

                                {/* Currency (Second Column) */}
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Currency')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <div className="mt-2">
                                        <input
                                            name="price.currency"
                                            readOnly
                                            value={CreateBoatDayPassFormValidation.values.price.currency}
                                            className="block w-full rounded-md border-gray-300 p-2 bg-gray-100 cursor-not-allowed"
                                        />
                                    </div>
                                    {CreateBoatDayPassFormValidation.touched.price?.currency &&
                                        CreateBoatDayPassFormValidation.errors.price?.currency && (
                                            <div className="text-red text-sm mt-1">
                                                {CreateBoatDayPassFormValidation.errors.price.currency}
                                            </div>
                                        )}
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Status')}
                                        <span className="text-red">*</span>
                                    </label>

                                    <select
                                        name="status_code"
                                        value={CreateBoatDayPassFormValidation.values.status_code || ''}
                                        onChange={CreateBoatDayPassFormValidation.handleChange}
                                        onBlur={CreateBoatDayPassFormValidation.handleBlur}
                                        className="block w-full rounded-md border-gray-300 p-2"
                                    >
                                        <option value="">{t('Select Status')}</option>
                                        {Object.entries(statusMap).map(([code, { label }]) => (
                                            <option key={code} value={code}>
                                                {t(label)}
                                            </option>
                                        ))}
                                    </select>

                                    {CreateBoatDayPassFormValidation.touched.status_code &&
                                        CreateBoatDayPassFormValidation.errors.status_code && (
                                            <div className="text-red text-sm mt-1">
                                                {CreateBoatDayPassFormValidation.errors.status_code}
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-3">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        type="submit"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </form>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={dialogError} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Saved Successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {/* Overlay and Loading Spinner */}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
